import { getCurrentSession } from "@/lib/supabase";
import type {
  TelegramAgentPermissions,
  TelegramChat,
  TelegramConnectionStatus,
  TelegramListParams,
  TelegramMessage,
} from "@/types/telegram";

function getBaseUrl(): string {
  return "/api/telegram";
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getCurrentSession();
  if (!session?.access_token) throw new Error("Please log in to access Telegram");
  return {
    Authorization: `Bearer ${session.access_token}`,
    "Content-Type": "application/json",
  };
}

async function parseError(response: Response): Promise<never> {
  let message = response.statusText || "Request failed";
  try {
    const body = await response.json();
    if (body?.error && typeof body.error === "string") message = body.error;
  } catch {
    /* ignore */
  }
  throw new Error(message);
}

class TelegramService {
  async getStatus(): Promise<TelegramConnectionStatus> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBaseUrl()}/auth/status`, { headers });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async sendCode(phone: string): Promise<{ phoneCodeHash: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBaseUrl()}/auth/send-code`, {
      method: "POST",
      headers,
      body: JSON.stringify({ phone }),
    });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async signIn(input: {
    phone: string;
    code: string;
    phoneCodeHash: string;
  }): Promise<{ connected: boolean; needsPassword?: boolean; name?: string; phone?: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBaseUrl()}/auth/sign-in`, {
      method: "POST",
      headers,
      body: JSON.stringify(input),
    });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async checkPassword(password: string): Promise<{ connected: boolean; name?: string; phone?: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBaseUrl()}/auth/check-password`, {
      method: "POST",
      headers,
      body: JSON.stringify({ password }),
    });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async disconnect(): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBaseUrl()}/auth/disconnect`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) await parseError(response);
  }

  async updatePermissions(
    agentPermissions: Partial<TelegramAgentPermissions>
  ): Promise<{ agentPermissions: TelegramAgentPermissions }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBaseUrl()}/auth/permissions`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ agentPermissions }),
    });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async listChats(params: TelegramListParams = {}): Promise<{ chats: TelegramChat[] }> {
    const qs = new URLSearchParams();
    if (params.limit) qs.set("limit", String(params.limit));
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBaseUrl()}/chats?${qs}`, { headers });
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async listMessages(
    chatId: string,
    params: TelegramListParams = {}
  ): Promise<{ messages: TelegramMessage[]; nextOffsetId?: number }> {
    const qs = new URLSearchParams();
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.offsetId) qs.set("offsetId", String(params.offsetId));
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${getBaseUrl()}/chats/${encodeURIComponent(chatId)}/messages?${qs}`,
      { headers }
    );
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async getMessage(chatId: string, messageId: string): Promise<TelegramMessage> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${getBaseUrl()}/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}`,
      { headers }
    );
    if (!response.ok) await parseError(response);
    const data = await response.json();
    return data.message;
  }

  async sendMessage(chatId: string, text: string): Promise<{ messageId: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${getBaseUrl()}/chats/${encodeURIComponent(chatId)}/messages`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ chatId, text }),
      }
    );
    if (!response.ok) await parseError(response);
    return response.json();
  }

  async searchMessages(
    query: string,
    params: TelegramListParams = {}
  ): Promise<{ messages: TelegramMessage[] }> {
    const qs = new URLSearchParams({ query });
    if (params.limit) qs.set("limit", String(params.limit));
    const headers = await getAuthHeaders();
    const response = await fetch(`${getBaseUrl()}/search?${qs}`, { headers });
    if (!response.ok) await parseError(response);
    return response.json();
  }
}

export const telegramService = new TelegramService();

export function telegramStatusQueryKey(userId: string | undefined) {
  return ["telegram-status", userId] as const;
}

export function telegramChatsQueryKey(userId: string | undefined, params?: TelegramListParams) {
  return ["telegram-chats", userId, params] as const;
}

export function telegramMessagesQueryKey(
  userId: string | undefined,
  chatId: string | null | undefined,
  params?: TelegramListParams
) {
  return ["telegram-messages", userId, chatId, params] as const;
}

export function telegramMessageQueryKey(
  chatId: string | undefined,
  messageId: string | undefined
) {
  return ["telegram-message", chatId, messageId] as const;
}
